import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from '../../../analytics/analytics-events';
import { REPORTING_STATUS_VIEW_PATH } from 'reporting/constants';
import { UserPermissionsEnum } from 'oas';
import { usePermission } from 'auth';

interface UseSingleActivityLogicProps {
    viewPath: string;
    selectedActivity: string;
    setSelectedActivity: (id: string) => void;
    firstActivityId?: string;
}

export const useSingleActivityLogic = ({
    viewPath,
    selectedActivity,
    setSelectedActivity,
    firstActivityId,
}: UseSingleActivityLogicProps) => {
    const history = useHistory();
    const location = useLocation();
    const match = useRouteMatch();
    const isSharedLinkLogin = usePermission(UserPermissionsEnum.SharedLink);

    // Path matching logic
    const path = viewPath?.replaceAll('/', '');
    const pathName = location.pathname.replaceAll('/', '');
    const isPathMatched = pathName === path?.replaceAll('/', '');
    const isActivityNotInUrl = !pathName.includes(selectedActivity ?? '');
    const isActivityNotSelected = !selectedActivity;
    const shouldSetDefault = (!selectedActivity && isPathMatched) || isActivityNotInUrl || isActivityNotSelected;
    const onStatusReport = match.url.includes(REPORTING_STATUS_VIEW_PATH);

    // Check if should render children only
    const shouldRenderChildrenOnly = isSharedLinkLogin || onStatusReport;

    // Handle default selection
    useEffect(() => {
        // on Status Report we don't want the activityId in the URL
        if (shouldSetDefault && firstActivityId && !onStatusReport) {
            history.replace(`${viewPath}/${firstActivityId}${location.search}`);
            setSelectedActivity(firstActivityId);
        }
    }, [shouldSetDefault, firstActivityId, history, viewPath, setSelectedActivity, onStatusReport, location.search]);

    // Selection handler
    const handleSelection = useCallback(
        (activityId: string) => {
            mixpanel.track(EVENTS.sideMenu.activitySelection, { activityId, type: 'single' });
            setSelectedActivity(activityId);
        },
        [setSelectedActivity],
    );

    // Path generator for navigation - preserves query params
    const getPath = useCallback(
        (activityId: string) => {
            const currentPath = history.location.pathname;
            const regex = new RegExp(viewPath ?? '', 'g');
            const splitPath = currentPath.split(regex);
            if (splitPath.length > 1) {
                const pathParams = splitPath[1].split('/').filter((element) => element !== '');
                pathParams[0] = activityId;
                const newPath = pathParams.join('/');
                return `${viewPath}/${newPath}${location.search}`;
            } else {
                return `${currentPath}${location.search}`;
            }
        },
        [history.location.pathname, viewPath, location.search],
    );

    // Check if activity is selected
    const isSelected = useCallback(
        (activityId: string) => {
            return selectedActivity === activityId || location.pathname.split('/').pop() === activityId;
        },
        [selectedActivity, location.pathname],
    );

    return useMemo(
        () => ({
            shouldSetDefault,
            shouldRenderChildrenOnly,
            handleSelection,
            getPath,
            isSelected,
        }),
        [shouldSetDefault, shouldRenderChildrenOnly, handleSelection, getPath, isSelected],
    );
};
